/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  FormControl,
  IconButton,
  FormHelperText as MuiFormHelperText,
  InputAdornment,
  Typography,
  styled,
  Link as MuiLink,
  Checkbox as MuiCheckbox,
  TextField as BaseTextField,
} from '@mui/material';
import {useFormik} from 'formik';
import Button from '@jobseeker/lotus/elements/Button';
import {useAlertCallback, useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useState} from 'react';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  BlankTickHirist,
  CheckHirist,
  Eye,
  HiddenEye,
  UnCheckIcon,
} from '@jobseeker/lotus/assets/icons';
import Dialog from '@jobseeker/lotus/components/DialogWithBottomSheet';
import * as yup from 'yup';
import Input from '@jobseeker/lotus/elements/Input';
import userService from '@jobseeker/common-core/services/user.service';
import {showErrorToast} from '@jobseeker/common-core/utils';
import Loader from '@jobseeker/lotus/components/Loader';
import {openUrl} from '@jobseeker/common-core/utils/common';
import {forgotPassword} from '@jobseeker/feature-authentication/helpers';
import {useSelector} from 'react-redux';
import type {AppState} from '@jobseeker/common-core/types';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';

const TextField = styled(BaseTextField)(({theme}) => ({
  '.MuiInputBase-root': {
    fontSize: 10,
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
  },
  fieldset: {
    borderColor: '#7E7E7E33',
  },
  '.Mui-focused': {
    '&.Mui-focused fieldset': {
      borderColor: '#e6e6e6',
      border: '2px solid',
    },
  },
}));

const IconImage = styled(Image)(({theme}) => ({
  '.Mui-focusVisible &': {
    outline: '2px auto',
    outlineColor: theme.palette.primary.light,
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor: theme.palette.mode === 'dark' ? '#30404d' : '#ebf1f5',
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    // cursor: 'not-allowed !important',
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(57,75,89,.5)'
        : 'rgba(206,217,224,.5)',
  },
}));

const FormHelperText = styled(MuiFormHelperText)(() => ({
  position: 'absolute',
}));
const Link = styled(MuiLink)(() => ({
  textDecoration: 'none',
  padding: 0,
  fontSize: 'inherit',
  cursor: 'pointer',
}));

const Checkbox = ({size, sx, ...props}: any) => {
  return (
    <MuiCheckbox
      sx={{
        '&:hover': {bgcolor: 'transparent'},
        width: 'fit-content',
        ...sx,
      }}
      disableRipple
      color="default"
      checkedIcon={
        <Image
          width={size ?? 20}
          height={size ?? 20}
          src={CheckHirist}
          alt="checkedIcon"
        />
      }
      icon={
        <IconImage
          width={size ?? 20}
          height={size ?? 20}
          src={UnCheckIcon}
          alt="UnCheckIcon"
        />
      }
      // inputProps={{}}
      {...props}
    />
  );
};

const CheckBoxWithLabel = ({title, subtitle, ...props}: any) => {
  return (
    <Box pt={2.5} display={'flex'} alignItems={'start'} gap={1}>
      <Checkbox
        icon={
          <Image src={BlankTickHirist} alt="unchecked" width={16} height={16} />
        }
        checkedIcon={
          <Image src={CheckHirist} alt="checked" width={16} height={16} />
        }
        size={16}
        sx={{
          '&.MuiCheckbox-root': {
            padding: 0,
            // marginTop: '-12px',
            display: 'inline-block',
          },
          // '.MuiFormControlLabel-label': {lineHeight: '0.8'},
          // '&.MuiFormControlLabel-root': {alignItems: 'flex-start'},
          // alignItems: 'flex-start',
        }}
        {...props}
      />
      <Box lineHeight={'18px'}>
        <Typography
          component={'p'}
          variant="subtitle2"
          color={'text.primary'}
          fontWeight={700}
          sx={{color: '#555'}}
        >
          {title}
        </Typography>
        <Typography component={'span'} variant="body2" color={'#787878'}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
};

const validationSchemaDeactivate = yup.object({
  foundJobViaIimjobs: yup.boolean(),
  foundJobViaOther: yup.boolean(),
  notGettingRelevantJobs: yup.boolean(),
  noValueForMemebrship: yup.boolean(),
  tooManyEmails: yup.boolean(),
  privacyConcerns: yup.boolean(),
  others: yup.boolean(),
  password: yup.string().required('Please enter your password'),
  textMsg: yup
    .string()
    // .test('textMsg', 'feedback is required', function (textMsg: any) {
    //   const {others} = this.parent;
    //   if (others && !textMsg) return false;
    //   else return true;
    // })
    .test(
      'textMsg',
      'Please enter a valid value. Spaces alone are not allowed.',
      function (textMsg: any) {
        if (textMsg === undefined) {
          return true;
        }
        return !!textMsg?.trim();
      }
    ),
});

const DeactivateForm = ({
  onSuccess,
  onError,
  onChange,
  onCancel,
  setIsFullScreen,
}: any) => {
  const theme = useWrappedTheme();
  const [isFeedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const profile = useSelector((state: AppState) => state.user.profile);
  const [ForgotAlert, toggleForgotAlert] = useAlertCallback({
    title: (
      <>
        We’ve sent an email to {profile.email || 'your email'}
        <br /> Click the link in the email to rest your password.
      </>
    ),
    subtitle:
      'If you don’t see the email, check other places it might be, like your junk, spam, social, or other folders.',
    primaryAction: 'Okay',
    primaryCallback: () => {},
    useBottomSheet: true,
  });

  const formik = useFormik({
    initialValues: {
      foundJobViaIimjobs: false,
      foundJobViaOther: false,
      notGettingRelevantJobs: false,
      noValueForMemebrship: false,
      tooManyEmails: false,
      privacyConcerns: false,
      others: false,
      textMsg: '',
      password: '',
    },
    validationSchema: validationSchemaDeactivate,
    onSubmit: async (values: any) => {
      const {
        foundJobViaIimjobs,
        foundJobViaOther,
        notGettingRelevantJobs,
        noValueForMemebrship,
        tooManyEmails,
        privacyConcerns,
        others,
      } = values;
      const bitsArray = [
        foundJobViaIimjobs,
        foundJobViaOther,
        notGettingRelevantJobs,
        noValueForMemebrship,
        tooManyEmails,
        privacyConcerns,
        others,
      ]
        .map(k => (k ? 1 : 0))
        .join('');

      const resp = await userService.deactivateAccount({
        password: values.password,
        reason: bitsArray,
        textMsg: values.textMsg,
      });

      if (resp?.error) {
        if (resp?.error?.message?.toLowerCase()?.includes('password')) {
          formik.setFieldError('password', resp?.error?.message);
        } else showErrorToast(resp.error.message, resp);
        onError();
      } else {
        onSuccess();
      }
    },
    validate: values => {
      const {
        foundJobViaIimjobs,
        foundJobViaOther,
        notGettingRelevantJobs,
        noValueForMemebrship,
        tooManyEmails,
        privacyConcerns,
        others,
      } = values;
      const passed = !(
        foundJobViaIimjobs ||
        foundJobViaOther ||
        notGettingRelevantJobs ||
        noValueForMemebrship ||
        tooManyEmails ||
        privacyConcerns ||
        others
      );
      return passed
        ? {anyOneRequired: 'Please select any one of the options'}
        : undefined;
    },
    // validateOnChange: true,
    validateOnBlur: true,
    // validateOnMount: true,
  });
  const submitFeedback = async () => {
    const validations = await formik.validateForm(formik.values);
    if (validations.anyOneRequired || validations.textMsg) {
      return;
    }
    onChange({reason: '0000000'});
    setFeedbackSubmitted(true);
    setIsFullScreen(false);
    formik.setErrors({password: ''});
  };

  return (
    <>
      <Box
        sx={{
          display: isFeedbackSubmitted ? 'none' : 'block',
        }}
      >
        <Box>
          <Box maxWidth={600}>
            <CheckBoxWithLabel
              title="Found a job via hirist.tech"
              subtitle="That’s great news! We’re happy to be of your service."
              name={'foundJobViaIimjobs'}
              onChange={formik.handleChange}
            />
            <CheckBoxWithLabel
              title="Found a job via some other Portal"
              subtitle={
                'Congratulations! We’re happy to be a part of your job-hunting journey.'
              }
              onChange={formik.handleChange}
              name={'foundJobViaOther'}
            />
            <CheckBoxWithLabel
              title="Not getting relevant jobs"
              subtitle={
                <>
                  You can always{' '}
                  <Link
                    href="/personalize-jobfeed"
                    target="_blank"
                    sx={{
                      color: theme.palette.primary.main,
                      textDecoration: 'underline',
                    }}
                  >
                    Personalize your jobfeed
                  </Link>{' '}
                  by selecting keywords related to your profile.
                </>
              }
              onChange={formik.handleChange}
              name={'notGettingRelevantJobs'}
            />
            <CheckBoxWithLabel
              title="Receiving too many E-mails"
              subtitle={
                <>
                  You can change your{' '}
                  <Link
                    href="/settings"
                    target="_blank"
                    sx={{
                      color: theme.palette.primary.main,
                      textDecoration: 'underline',
                    }}
                  >
                    mail notification settings
                  </Link>{' '}
                  and unsubscribe from mails you do not wish to receive.
                </>
              }
              onChange={formik.handleChange}
              name={'tooManyEmails'}
            />
            <CheckBoxWithLabel
              title="Privacy Concerns"
              subtitle={
                <>
                  You can{' '}
                  <Link
                    href="/settings?section=privacy"
                    target="_blank"
                    sx={{
                      color: theme.palette.primary.main,
                      textDecoration: 'underline',
                    }}
                  >
                    block
                  </Link>{' '}
                  any company/recruiter from viewing your profile that you wish
                  to. Also, you can{' '}
                  <Link
                    href="/settings?section=privacy"
                    target="_blank"
                    sx={{
                      color: theme.palette.primary.main,
                      textDecoration: 'underline',
                    }}
                  >
                    hide your profile
                  </Link>{' '}
                  from all recruiter/companies except who you explicitly apply
                  against.
                </>
              }
              onChange={formik.handleChange}
              name={'privacyConcerns'}
            />
            <CheckBoxWithLabel
              title="Others"
              subtitle=""
              onChange={formik.handleChange}
              name={'others'}
            />
          </Box>

          <TextField
            name={'textMsg'}
            value={formik.values.textMsg}
            onChange={formik.handleChange}
            multiline
            maxRows={4}
            inputProps={{
              style: {
                padding: '10px 15px',
                height: '100%',
              },
            }}
            rows={4}
            sx={{
              width: '100%',
              backgroundColor: formik.values.others ? 'transparent' : '#f5f5f5',
              transition: 'background-color 0.3s ease',
              '& .MuiInputBase-input': {
                fontSize: {xxs: '14px', md: '16px'},
                lineHeight: {xxs: '18px', md: '21px'},
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#ccc',
                },
                '&:hover fieldset': {
                  borderColor: '#ccc',
                },
                '&.Mui-focused fieldset': {
                  borderColor: {xxs: theme.palette.primary.main, md: '#000'},
                },
              },
              'textarea::placeholder': {
                fontStyle: 'normal',
              },
            }}
            placeholder="Your feedback is important to us, Please tell us how we can improve."
            variant="outlined"
          />
        </Box>
        {formik.touched && (
          <Box display={'flex'} mt={1}>
            <Typography variant="caption" color="error">
              {formik.errors.anyOneRequired || formik.errors.textMsg}
            </Typography>
          </Box>
        )}
      </Box>
      {isFeedbackSubmitted && (
        <Box display={'flex'} mt={2} justifyContent={'space-between'}>
          <Typography fontSize={14}>Password</Typography>
        </Box>
      )}

      <FormControl
        sx={{
          width: {xxs: '100%', md: '70%'},
          mt: 0.5,
          display: !isFeedbackSubmitted ? 'none' : 'block',
        }}
        error={Boolean(formik.errors.password)}
      >
        {/* <Typography variant="subtitle3">Enter the password</Typography> */}
        <Input
          variant={VersionVariants.VERSION_ONE}
          type={showNewPassword ? 'text' : 'Password'}
          name="password"
          value={formik.values.password}
          onChange={formik.handleChange}
          error={Boolean(formik.errors.password)}
          placeholder="Enter the password"
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#ccc',
              },
              '&:hover fieldset': {
                borderColor: '#ccc',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#000',
              },
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  // onMouseDown={handleMouseDown}
                  // edge="end"
                >
                  {showNewPassword ? (
                    <Image
                      src={Eye}
                      width={15}
                      height={15}
                      alt="show password"
                    />
                  ) : (
                    <Image
                      src={HiddenEye}
                      width={15}
                      height={15}
                      alt="hide password"
                    />
                  )}
                </IconButton>
              </InputAdornment>
            ),
            autoComplete: 'new-password',
          }}
        />

        <Box display={'flex'} justifyContent={'space-between'}>
          <FormHelperText
            sx={{
              opacity: Boolean(formik.errors.password) ? 1 : 0,
              width: '100%',
            }}
          >
            <>{formik?.errors?.password || ''}</>
          </FormHelperText>
        </Box>
        <Box
          display={'flex'}
          justifyContent={'flex-end'}
          mt={1}
          sx={{
            textDecoration: 'none',
            cursor: 'pointer',
            display: 'block',
            fontSize: '16px',
            mt: {xxs: '8px', md: 0},
            fontWeight: 400,
            textAlign: 'right',
            fontFamily: 'Nunito Sans',
          }}
        >
          <Link
            onClick={async () => {
              try {
                const res = await forgotPassword(profile.email);
                toggleForgotAlert();
              } catch (err: any) {
                showErrorToast(
                  err?.message ||
                    'Failed to send reset email. Please try again.',
                  err
                );
              }
            }}
            color={theme.palette.primary.main}
          >
            Forgot Password?
          </Link>
        </Box>
      </FormControl>
      <Box
        display={'flex'}
        sx={{
          flexDirection: isFeedbackSubmitted
            ? {xxs: 'column', md: 'row'}
            : 'row',
        }}
        gap={1.5}
        mt={4}
        mb={2}
      >
        <Button
          sx={{
            fontSize: '16px',
            backgroundColor: theme.palette.primary.main,
            px: 5,
            py: 1.25,
            fontWeight: 500,
            height: 'fit-content',
            borderRadius: '3px',
            '&:hover': {
              color: '#fff',
              backgroundColor: '#e79e00',
            },
            '&:focus': {
              backgroundColor: theme.palette.primary.main,
            },
            '&:active': {
              backgroundColor: theme.palette.primary.main,
            },
          }}
          variant="contained"
          onClick={isFeedbackSubmitted ? formik.handleSubmit : submitFeedback}
          disabled={
            !isFeedbackSubmitted
              ? Boolean(formik.errors.textMsg) ||
                Boolean(formik.errors.anyOneRequired)
              : Boolean(formik.errors.password)
          }
        >
          {!isFeedbackSubmitted ? 'Submit' : 'Deactivate'}
        </Button>
        {isFeedbackSubmitted && (
          <Button
            onClick={onCancel}
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              color: theme.palette.primary.main,
            }}
            variant="text"
          >
            Cancel
          </Button>
        )}
      </Box>
      {}
      <ForgotAlert />
    </>
  );
};

export const DeavtivateComponent: React.FC<object> = ({}) => {
  const initialHeaders = {
    title: (
      <Typography fontSize={20} fontWeight={700} mb={1}>
        Deactivate my Account
      </Typography>
    ),
    subtitle: (
      <Typography fontSize={18} fontWeight={200}>
        We’re sorry to see you go, please tell us your reason for deactivating.
      </Typography>
    ),
    content: <></>,
  };

  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [alertHeaders, setAlertHeaders] = useState(initialHeaders);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const isLarge = useIsLargeDevice();

  const toggleDeactivateDialog = () => {
    setDeactivateDialogOpen(prev => !prev);
    setIsFullScreen(isLarge ? false : true);
    setTimeout(() => {
      setAlertHeaders(initialHeaders);
    }, 50);
  };

  const onChange = (values: any) => {
    if (values.reason) {
      setAlertHeaders({
        title: (
          <Typography fontSize={20} fontWeight={700} mb={1}>
            Deactivate Account
          </Typography>
        ),
        subtitle: (
          <Typography
            fontSize={{xxs: 16, md: 18}}
            fontWeight={200}
            lineHeight={1.68}
          >
            Deactivating your account would withdraw all applications, your
            profile will not be shown to the recruiters, and you will stop
            receiving our mailer services.
          </Typography>
        ),
        content: (
          <Typography fontSize={{xxs: 14, md: 16}} mt={2} mb={3.5}>
            Please provide your password if you still wish to deactivate your
            profile.
          </Typography>
        ),
      });
      setIsFullScreen(false);
    }
  };

  const onSuccess = async () => {
    toggleLoaderDialog();
    toggleDeactivateDialog();
    openUrl('/deactivate_success', '_self');
  };

  const [LoaderDialog, toggleLoaderDialog] = useAlertCallback({
    alert: true,
    hideActions: true,
    forHirist: true,
    primaryCallback: '',
    BodyContent: () => (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <Loader />
        <Typography>Deactivating Account</Typography>
      </Box>
    ),
  });

  return (
    <>
      <Dialog
        open={deactivateDialogOpen}
        onClose={toggleDeactivateDialog}
        title={alertHeaders.title}
        subTitle={alertHeaders.subtitle}
        Content={alertHeaders.content}
        useBottomSheet={false}
        fullScreen={isFullScreen}
        sx={{
          '& .MuiDialog-paper': {
            minWidth: {
              xxs: isFullScreen ? '100vw' : 'calc(100vw - 16px)',
              md: '500px',
            },
            maxWidth: {
              xxs: isFullScreen ? '100vw' : 'calc(100vw - 16px)',
              md: '700px',
            },
            width: {
              xxs: isFullScreen ? '100vw' : 'calc(100vw - 16px)',
              md: '700px',
            },
            borderRadius: {xxs: isFullScreen ? '0px' : '8px', md: '8px'},
          },
        }}
      >
        <DeactivateForm
          onSuccess={onSuccess}
          onChange={onChange}
          setIsFullScreen={setIsFullScreen}
          onError={console.log}
          onCancel={toggleDeactivateDialog}
        />
      </Dialog>
      <Typography
        display="flex"
        mt={{xxs: 7, md: 2}}
        onClick={toggleDeactivateDialog}
        justifyContent={'center'}
        fontSize={{xxs: 14, md: 16}}
        fontWeight={{xxs: 400, md: 400}}
        sx={{
          cursor: 'pointer',
          color: '#0057ff',
          wordWrap: 'break-word',
          textTransform: 'capitalize',
        }}
      >
        Deactivate Account?
      </Typography>
      <LoaderDialog />
    </>
  );
};
